import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { AuthenticationService } from '../../../shared/services/authentication.service';
import { CartService } from '../../../shared/services/cart.service';
import { ModalService } from '../../../shared/services/modal.service';

/**
 * Landing route for the Google sign-in redirect.
 *
 * Google -> backend /auth/google/callback -> here. That is a browser redirect,
 * not an XHR, so the backend has no response body to answer with and passes the
 * session through the URL instead.
 *
 * It arrives in the URL *fragment*, and that placement is load-bearing: a query
 * string is written to the frontend host's access log, kept in browser history,
 * and sent as the Referer on every third-party request the page makes -- and
 * index.html fires Google Tag Manager on page load. A session in the query
 * string would therefore be handed to Google Analytics on every sign-in. A
 * fragment is never sent to any server and never appears in Referer.
 *
 * Errors are the exception and stay in the query string: they carry no
 * credentials, only a message to show.
 */
@Component({
    selector: 'indiapicture-auth-callback',
    templateUrl: './auth-callback.component.html'
})
export class AuthCallbackComponent implements OnInit {

    /**
     * Where to return after the round trip, written by whoever sent the browser
     * to Google. Must match the key the AI Studio parks under.
     */
    private static readonly RETURN_KEY = 'ip.auth.returnTo';

    /*
     * Where a Google sign-in lands when there is nothing parked to go back to,
     * matching POST_AUTH_PATH in the sign-in modal.
     *
     * Only the FALLBACK changes. A parked path still wins, so somebody sent here
     * from the checkout still returns to the checkout — losing that would strand
     * them a page away from what they were doing.
     */
    private static readonly FALLBACK_PATH = '/ai';

    constructor(private route: ActivatedRoute,
                private router: Router,
                private authService: AuthenticationService,
                private cartService: CartService,
                private modalService: ModalService,
                private toastrService: ToastrService) {
    }

    ngOnInit(): void {
        const error = this.route.snapshot.queryParamMap.get('error');

        if (error) {
            this.fail(error);
            return;
        }

        const encoded = this.readFragmentParam('user');

        if (!encoded) {
            this.fail('Google sign in did not complete. Please try again.');
            return;
        }

        let payload;

        try {
            payload = JSON.parse(this.decodeBase64Utf8(encoded));
        } catch (e) {
            this.fail('Google sign in could not be read. Please try again.');
            return;
        }

        this.authService.applyRedirectSession({ user: payload, cart: null }).then(signedIn => {
            if (!signedIn) {
                this.fail('Could not sign you in just now. Please try again.');
                return;
            }

            /* A redirect payload carries no cart -- the password and OTP flows
             * get one in the response body, this one has to ask for it. */
            this.cartService.getCartList();

            this.toastrService.success('Signed in with Google');
            this.navigateAway();
        });
    }

    /*
     * The fragment is `user=<base64>` in query-string form, so it parses the
     * same way. The backend percent-encodes it, which matters: raw base64 can
     * contain '+', and URLSearchParams would read that as a space.
     */
    private readFragmentParam(name: string): string | null {
        const fragment = this.route.snapshot.fragment;

        if (!fragment) {
            return null;
        }

        return new URLSearchParams(fragment).get(name);
    }

    /*
     * atob() yields one byte per character, so a multi-byte UTF-8 sequence -- an
     * accented name, say -- comes back mangled unless the bytes are decoded as
     * UTF-8. TextDecoder does that; the older idiom for this was
     * decodeURIComponent(escape(...)), but escape() is Annex B legacy.
     */
    private decodeBase64Utf8(encoded: string): string {
        const bytes = Uint8Array.from(atob(encoded), char => char.charCodeAt(0));

        return new TextDecoder().decode(bytes);
    }

    private fail(message: string): void {
        this.toastrService.error(message);
        this.navigateAway().then(() => this.modalService.showLoginModal());
    }

    /*
     * replaceUrl so the URL that carried the session is not left in history for
     * the back button to return to. navigate() drops the fragment with it.
     *
     * Home unless the page that started the round trip parked somewhere to come
     * back to. The AI Studio does: Google is a full-page departure, and landing
     * a signed-in visitor on the homepage reads as the sign-in having failed.
     */
    private navigateAway(): Promise<boolean> {
        return this.router.navigateByUrl(this.takeReturnUrl(), { replaceUrl: true });
    }

    /*
     * Read once and cleared, so a stale entry cannot hijack a later sign-in.
     *
     * Only a same-origin path is honoured: it has to start with a single '/',
     * because '//host' and 'https://host' are both absolute in navigateByUrl and
     * would turn a parked value into an open redirect if anything else ever
     * wrote this key.
     */
    private takeReturnUrl(): string {
        let parked: string | null = null;

        try {
            parked = sessionStorage.getItem(AuthCallbackComponent.RETURN_KEY);
            sessionStorage.removeItem(AuthCallbackComponent.RETURN_KEY);
        } catch (e) {
            return AuthCallbackComponent.FALLBACK_PATH;
        }

        return (parked && /^\/(?!\/)/.test(parked)) ? parked : AuthCallbackComponent.FALLBACK_PATH;
    }
}
